import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, ClipboardCheck, UserCheck, Search, Handshake, ShieldCheck, DollarSign, Users, ChevronLeft, ChevronRight, Phone, Mail } from 'lucide-react';
import HssDifferenceTable from '../components/HssDifferenceTable';
import NewMemberToast from '../components/NewMemberToast';
import { cloudinaryOptimize } from '../utils/cloudinaryOptimize';
import './LandingPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_SINGLE_PENSION = 674;
const MAX_COUPLE_PENSION = 1009;

const LandingPage = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselPaused, setCarouselPaused] = useState(false);
  const [infoTab, setInfoTab] = useState('fees');
  const [activeModal, setActiveModal] = useState(null);
  const [envoySpots, setEnvoySpots] = useState(null);
  const [propertyCount, setPropertyCount] = useState(null);

  const openModal = (tab) => { setInfoTab(tab); setActiveModal(tab); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/properties`).then(res => setProperties(res.data)).catch(() => {});
    axios.get(`${API}/applications/envoy-count`).then(res => setEnvoySpots(res.data)).catch(() => {});
    axios.get(`${API}/properties/count`).then(res => setPropertyCount(res.data?.count ?? null)).catch(() => {});
  }, []);

  // Only show properties that have at least one photo in the carousel
  const propertiesWithPhotos = useMemo(
    () => properties.filter(p => p.images && p.images.length > 0),
    [properties]
  );

  const visibleCount = 1;
  const maxIndex = Math.max(0, propertiesWithPhotos.length - visibleCount);

  const nextSlide = useCallback(() => {
    setCarouselIndex(i => i >= maxIndex ? 0 : i + 1);
  }, [maxIndex]);

  const prevSlide = () => setCarouselIndex(i => i <= 0 ? maxIndex : i - 1);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (propertiesWithPhotos.length <= visibleCount) return;
    if (carouselPaused) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [propertiesWithPhotos.length, nextSlide, carouselPaused]);

  // Cheapest property for the hero "$63 per week TODAY!" link
  const cheapestProperty = useMemo(() => {
    if (!properties.length) return null;
    return properties
      .filter(p => p.weekly_rent_per_person > 0)
      .reduce((min, p) => p.weekly_rent_per_person < (min?.weekly_rent_per_person ?? Infinity) ? p : min, null);
  }, [properties]);

  // Lowest net rent after Singles CRA — dynamic, based on cheapest property
  const lowestNetRent = useMemo(() => {
    if (!cheapestProperty) return null;
    const r = cheapestProperty.weekly_rent_per_person;
    const cra = Math.min(73.23, Math.max(0, 0.75 * (r - 74.10)));
    return Math.round(r - cra);
  }, [cheapestProperty]);

  const stats = useMemo(() => {
    if (!properties.length) return null;
    const rents = properties.map(p => p.weekly_rent_per_person).filter(r => r > 0);
    const avgRent = rents.reduce((s, r) => s + r, 0) / rents.length;
    const totalRents = properties.filter(p => p.total_bedrooms).map(p => p.weekly_rent_per_person * p.total_bedrooms);
    const avgGross = totalRents.length ? totalRents.reduce((s, r) => s + r, 0) / totalRents.length : 0;
    const sCRA = Math.min(73.23, Math.max(0, 0.75 * (avgRent - 74.10)));
    const cCRA = Math.min(103.40, Math.max(0, 0.75 * (avgRent - 123.10)));
    const sNet = Math.round(avgRent - sCRA);
    const cNet = Math.round(avgRent - cCRA);
    return {
      avgSingleRent: sNet,
      avgCouplesRent: cNet,
      singleRentPct: ((sNet / MAX_SINGLE_PENSION) * 100).toFixed(1),
      coupleRentPct: ((cNet / MAX_COUPLE_PENSION) * 100).toFixed(1),
      avgGrossRent: Math.round(avgGross),
      avgRentPerBed: Math.round(avgRent),
      maxSinglesCRA: 73.23,
      maxCouplesCRA: 103.40,
    };
  }, [properties]);

  return (
    <div className="landing-page">
      {/* Combined Header + Full Nav */}
      <header className="landing-header">
        <div className="header-inner">
          <div className="logo">
            <img src="/logo.png" alt="House Sharing Seniors" className="logo-image" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
          </div>
          <div className="nav-cta-buttons">
            <button className="nav-btn nav-btn-login" onClick={() => navigate('/login')} data-testid="sitenav-login">Member Login</button>
            <button className="nav-btn nav-btn-join" onClick={() => navigate('/apply')} data-testid="sitenav-join">JOIN HSS NOW</button>
            <button className="nav-btn nav-btn-guest" onClick={() => navigate('/properties')} data-testid="sitenav-browse-guest">Browse as Guest</button>
          </div>
          <nav className="main-nav" data-testid="main-nav">
            <div className="nav-row">
              <a className="nav-link" href="/properties" data-testid="sitenav-properties">Properties</a>
              <button className="nav-link" onClick={() => navigate('/rent-calculator')} data-testid="sitenav-rent-calculator">Rent Calculator</button>
              <a className="nav-link" href="/best-locations" data-testid="sitenav-best-locations">Best Locations</a>
              <button className="nav-link" onClick={() => openModal('fees')} data-testid="sitenav-member-fees">Member Fees</button>
              <a className="nav-link" href="/rea" data-testid="sitenav-rea">REA Information</a>
              <a className="nav-link" href="/about" data-testid="sitenav-about">About</a>
              <a className="nav-link" href="/contact" data-testid="sitenav-contact">Contact</a>
              <a className="nav-link" href="/support" data-testid="sitenav-support">Support</a>
            </div>
            <div className="nav-row">
              <a className="nav-link" href="/testimonials" data-testid="sitenav-testimonials">Testimonials</a>
              <a className="nav-link" href="#how-it-works" data-testid="sitenav-how-works">How HSS Works</a>
              <a className="nav-link" href="/features" data-testid="sitenav-features">HSS Features</a>
              <a className="nav-link" href="/faq" data-testid="sitenav-faq">HSS FAQ</a>
              <button className="nav-link" onClick={() => openModal('cra')} data-testid="sitenav-rent-assistance">Rent Assistance</button>
              <a className="nav-link" href="/bond-loan" data-testid="sitenav-bond-loan">Free Bond Loan</a>
              <a className="nav-link" href="/resources" data-testid="sitenav-resources">Member Resources</a>
              <a className="nav-link" href="/investors" data-testid="sitenav-investors">Investors</a>
              <button className="nav-link" onClick={() => openModal('job_support')} data-testid="sitenav-jobs">HSS Jobs</button>
            </div>
          </nav>
        </div>
      </header>

      {/* Nav Dropdown Panel — appears directly under sticky nav */}
      {activeModal && (
        <div className="nav-dropdown-panel" data-testid="nav-dropdown-panel">
          <div className="nav-dropdown-inner">
            <div className="nav-dropdown-tabs">
              {infoTab === 'job_support' || infoTab === 'job_manager' ? (
                <>
                  <button className={`nav-dropdown-tab${infoTab === 'job_support' ? ' active' : ''}`} onClick={() => setInfoTab('job_support')} data-testid="jobs-tab-support">Member Support Role</button>
                  <button className={`nav-dropdown-tab${infoTab === 'job_manager' ? ' active' : ''}`} onClick={() => setInfoTab('job_manager')} data-testid="jobs-tab-manager">Community Manager Role</button>
                  <a className="nav-dropdown-tab" href="mailto:jobs@housesharingseniors.com.au?subject=Expression%20of%20Interest%20-%20HSS%20Jobs" data-testid="jobs-eoi-email-link">&#9993; Email Your Interest</a>
                </>
              ) : (
                <>
                  <button className={`nav-dropdown-tab${infoTab === 'fees' ? ' active' : ''}`} onClick={() => setInfoTab('fees')}>Member Fees</button>
                  <button className={`nav-dropdown-tab${infoTab === 'cra' ? ' active' : ''}`} onClick={() => setInfoTab('cra')}>Rent Assistance (CRA)</button>
                </>
              )}
              <button className="nav-dropdown-close" onClick={() => setActiveModal(null)} aria-label="Close">&times;</button>
            </div>
            {infoTab === 'fees' && (
              <div className="nav-dropdown-content" data-testid="member-fees-content">
                <div className="fees-cards">
                  <div className="fee-card"><div className="fee-card-label">SINGLE MEMBER<sup>#</sup></div><div className="fee-card-price">$A190<sup>*</sup></div><div className="fee-card-period">6 months</div></div>
                  <div className="fee-card fee-card-couple"><div className="fee-card-label">COUPLE MEMBER</div><div className="fee-card-price">$A290<sup>*</sup></div><div className="fee-card-period">6 months</div></div>
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  <p><sup>*</sup> Pending any Govt assistance or subsidy &nbsp;|&nbsp; <sup>#</sup> Singles use one bedroom just like Couples.</p>
                  <p>No per-group charges. Payment after application is approved.</p>
                </div>
              </div>
            )}
            {infoTab === 'cra' && (
              <div className="nav-dropdown-content" data-testid="rent-assistance-content">
                <p style={{ fontSize: '13px', color: '#4b5563', marginBottom: '10px' }}>CRA is a Government payment helping pensioners with rent. Each housemate qualifies individually &mdash; dramatically reducing out-of-pocket rent.</p>
                <div style={{ overflowX: 'auto' }}>
                  <table className="cra-detail-table">
                    <thead><tr><th></th><th>Min Rent p/wk<br/>for any CRA</th><th>Max CRA p/wk</th><th>Min Rent p/wk<br/>for Max CRA</th><th>Gross Rent p/wk</th><th>CRA p/wk</th><th>Net Rent p/wk</th></tr></thead>
                    <tbody>
                      <tr><td><strong>Single Sharing</strong></td><td>$74.10</td><td>$73.23</td><td>$171.74</td><td>$250</td><td>$73.23</td><td className="cra-net-green"><strong>$176.77</strong></td></tr>
                      <tr><td><strong>Couples Sharing</strong></td><td>$123.10</td><td>$103.40</td><td>$260.97</td><td>$250</td><td>$95.18</td><td className="cra-net-green"><strong>$154.83</strong></td></tr>
                    </tbody>
                  </table>
                </div>
                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>Example: $1,000/4-bed = $250/bed/wk. Sep 2024 rates. Seek Centrelink advice.</p>
              </div>
            )}
            {infoTab === 'job_support' && (
              <div className="nav-dropdown-content" data-testid="jobs-support-content">
                <div style={{ maxWidth: '620px', fontSize: '13px', color: '#374151' }}>
                  <h4 style={{ margin: '0 0 6px', color: '#1a2332' }}>HSS Member Support — Part-Time, 1 Day (6 hrs) / Week</h4>
                  <p style={{ margin: '0 0 8px' }}>Be the first point of contact for our members &mdash; phone and email only, no face-to-face. On-site at our Broadbeach&ndash;Southport hub (Gold Coast QLD), not remote.</p>
                  <p style={{ margin: '0 0 8px' }}><strong>Pay:</strong> $34.44/hr (SCHADS Level 1) &mdash; about $206/week before tax, plus super. Designed to fit within the Age Pension Work Bonus.</p>
                  <a href="mailto:jobs@housesharingseniors.com.au?subject=Member%20Support%20Job%20Application" className="btn btn-primary" style={{ display: 'inline-block', marginTop: '6px' }} data-testid="jobs-apply-support">Apply by Email</a>
                </div>
              </div>
            )}
            {infoTab === 'job_manager' && (
              <div className="nav-dropdown-content" data-testid="jobs-manager-content">
                <div style={{ maxWidth: '620px', fontSize: '13px', color: '#374151' }}>
                  <h4 style={{ margin: '0 0 6px', color: '#1a2332' }}>HSS Community Manager — Permanent, 5 Days &times; 6 Hours / Week</h4>
                  <p style={{ margin: '0 0 8px' }}>Lead our Member Support team and handle escalations, complaints and member safety. On-site at our Broadbeach&ndash;Southport hub (Gold Coast QLD), not remote.</p>
                  <p style={{ margin: '0 0 8px' }}><strong>Pay:</strong> $53.43/hr (SCHADS Level 5) &mdash; about $1,603/week before tax, plus super.</p>
                  <a href="mailto:jobs@housesharingseniors.com.au?subject=Community%20Manager%20Application" className="btn btn-primary" style={{ display: 'inline-block', marginTop: '6px' }} data-testid="jobs-apply-manager">Apply by Email</a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gold Envoy Launch Banner */}
      {envoySpots && envoySpots.offer_active && (() => {
        const cap = envoySpots.envoy_cap || 100;
        const left = Math.min(envoySpots.spots_remaining, cap);
        const taken = envoySpots.spots_taken;
        const pct = ((cap - left) / cap) * 100;
        const urgency = left <= 3 ? 'critical' : left <= 8 ? 'urgent' : left <= 15 ? 'warm' : 'fresh';
        const colors = {
          fresh:    { bg: '#dc2626', accent: 'white', text: 'white' },
          warm:     { bg: '#dc2626', accent: 'white', text: 'white' },
          urgent:   { bg: '#b91c1c', accent: 'white', text: 'white' },
          critical: { bg: '#7f1d1d', accent: 'white', text: 'white' },
        }[urgency];
        const countMsg = left <= 2 ? `ONLY ${left} LEFT!` : left <= 5 ? `JUST ${left} SPOTS LEFT!` : left <= 10 ? `${left} SPOTS REMAINING!` : left <= 15 ? `${left} SPOTS LEFT — MORE THAN HALF GONE!` : `${left} of ${cap} spots remaining`;
        return (
          <div style={{ background: colors.bg, padding: '32px 0', position: 'relative', overflow: 'hidden', borderBottom: '5px solid white' }} data-testid="envoy-banner">
            {/* Progress bar */}
            <div style={{ position: 'absolute', bottom: 0, left: 0, height: '8px', width: `${pct}%`, background: 'white', transition: 'width 0.5s ease' }} />
            <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ background: 'white', color: '#dc2626', fontWeight: '900', fontSize: '20px', padding: '10px 20px', borderRadius: '24px', whiteSpace: 'nowrap', letterSpacing: '0.05em' }}>
                  ★ LAUNCH OFFER
                </div>
                <div>
                  <div style={{ color: 'white', fontSize: '28px', fontWeight: '900', lineHeight: 1.2 }}>
                    90% Off First {cap} Applications &mdash; <span style={{ color: 'white', fontWeight: '900' }}>$20 Gold Envoy Lifetime Membership</span>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '15px', marginTop: '5px' }}>
                    Reduced from normal $190 (Single) / $290 (Couples) &nbsp;·&nbsp; {taken} applications received so far
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                <div style={{ color: 'white', fontWeight: '900', fontSize: '28px', whiteSpace: 'nowrap' }}>
                  {countMsg}
                </div>
                <button onClick={() => navigate('/apply')}
                  style={{ background: 'white', color: '#dc2626', border: 'none', borderRadius: '10px', padding: '16px 32px', fontWeight: '900', fontSize: '20px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  Apply Now &rarr;
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Hero — Two Column */}<section className="hero-section">
        <div className="container">
          <div className="hero-layout">
            <div className="hero-left">
              <div className="hero-eyebrow" data-testid="hero-eyebrow">For Australian Age Pensioners</div>
              <h1 className="hero-title" data-testid="hero-title">
                Share a premium property<br />with other seniors
              </h1>
              <div data-testid="special-offer-box" style={{ background: '#dc2626', borderRadius: '12px', padding: '20px 12px 26px', margin: '24px 0 4px', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '14px', boxShadow: '0 4px 16px rgba(220,38,38,0.25)', position: 'relative', overflow: 'hidden', borderBottom: '4px solid #b91c1c' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div style={{ background: 'white', color: '#dc2626', fontWeight: '900', fontSize: '26px', padding: '14px 42px', borderRadius: '36px', whiteSpace: 'nowrap', letterSpacing: '0.05em' }}>
                    ★ SPECIAL OFFER
                  </div>
                </div>
                <div style={{ color: 'white', fontSize: '21px', fontWeight: '900', lineHeight: 1.2, whiteSpace: 'nowrap', letterSpacing: '-0.01em', textAlign: 'left' }}>
                  Super Special Start-up Offer for Pensioners! For a limited time only.
                </div>
                <div style={{ color: 'white', fontSize: '22px', fontWeight: '800', lineHeight: 1.2, whiteSpace: 'nowrap', letterSpacing: '-0.01em', textAlign: 'left' }}>
                  Single Pensioner Membership Fee reduced by $100 to just $190!
                </div>
                <div style={{ color: 'white', fontSize: '22px', fontWeight: '800', lineHeight: 1.2, whiteSpace: 'nowrap', letterSpacing: '-0.01em', textAlign: 'left' }}>
                  Couples Pensioner Membership Fee reduced by $100 to just $290!
                </div>
              </div>
              <p className="hero-price">
                <em>And pay net rent from &hellip;<br /><span className="hero-price-amount">${lowestNetRent || '—'} per week TODAY!</span> <a href={cheapestProperty ? `/properties/${cheapestProperty.property_id}` : '/properties'} onClick={(e) => { e.preventDefault(); navigate(cheapestProperty ? `/properties/${cheapestProperty.property_id}` : '/properties'); }} style={{ fontSize: '14px', fontWeight: '700', color: '#2563eb', textDecoration: 'none', borderBottom: '1px solid #2563eb', whiteSpace: 'nowrap', cursor: 'pointer' }} data-testid="hero-cheapest-property-link">Check now &rarr;</a></em>
              </p>
              <p className="hero-subtitle" data-testid="hero-subtitle">
                <strong>Shared housing for Australian Age Pensioners</strong> — a community enterprise helping seniors to drastically reduce their rent and weekly living costs through safe, vetted, curated shared housing in premium properties &amp; locations, without giving up independence.
              </p>
              <div className="hero-cta">
                <button className="btn btn-primary" onClick={() => navigate('/apply')} data-testid="get-started-btn">
                  Start Your HSS Membership Application &rarr;
                </button>
                <button className="btn btn-secondary" onClick={() => navigate('/apply?resume=true')} data-testid="resume-app-btn">
                  Resume Your Application &rarr;
                </button>
              </div>
            </div>

            {/* AVAILABLE NOW Panel */}
            <div className="hero-right">
              <div className="available-now-panel" data-testid="available-now-panel">
                <div className="an-title">AVAILABLE NOW</div>
                <div className="an-count">{propertyCount ?? properties.length ?? '—'}</div>
                <div className="an-subtitle">Premium properties</div>
                <div className="an-grid">
                  <div className="an-stat an-stat-red">
                    <div className="an-stat-value">${stats?.avgSingleRent || '—'}</div>
                    <div className="an-stat-label"><span style={{display:'block'}}>Avg HSS Single Rent</span><span style={{display:'block'}}>p/wk</span></div>
                  </div>
                  <div className="an-stat an-stat-red">
                    <div className="an-stat-value">${stats?.avgCouplesRent || '—'}</div>
                    <div className="an-stat-label"><span style={{display:'block'}}>Avg HSS Couples Rent</span><span style={{display:'block'}}>p/wk</span></div>
                  </div>
                  <div className="an-stat an-stat-white">
                    <div className="an-stat-value">${MAX_SINGLE_PENSION}</div>
                    <div className="an-stat-label"><span style={{display:'block'}}>Max Single Pension</span><span style={{display:'block'}}>(with Max CRA)</span></div>
                  </div>
                  <div className="an-stat an-stat-white">
                    <div className="an-stat-value">${MAX_COUPLE_PENSION}</div>
                    <div className="an-stat-label"><span style={{display:'block'}}>Max Couple Pension</span><span style={{display:'block'}}>(with Max CRA)</span></div>
                  </div>
                  <div className="an-stat an-stat-red">
                    <div className="an-stat-value">{stats?.singleRentPct || '—'}%</div>
                    <div className="an-stat-label"><span style={{display:'block'}}>Single Net Rent</span><span style={{display:'block'}}>as % of Max Pension</span></div>
                  </div>
                  <div className="an-stat an-stat-red">
                    <div className="an-stat-value">{stats?.coupleRentPct || '—'}%</div>
                    <div className="an-stat-label"><span style={{display:'block'}}>Couple Net Rent</span><span style={{display:'block'}}>as % of Max Pension</span></div>
                  </div>
                  {stats?.avgGrossRent > 0 && (
                    <div className="an-stat an-stat-white">
                      <div className="an-stat-value">${stats.avgGrossRent}</div>
                      <div className="an-stat-label"><span style={{display:'block'}}>Avg gross</span><span style={{display:'block'}}>rent/wk</span></div>
                    </div>
                  )}
                  <div className="an-stat an-stat-white">
                    <div className="an-stat-value">${stats?.avgRentPerBed || '—'}</div>
                    <div className="an-stat-label"><span style={{display:'block'}}>Avg</span><span style={{display:'block'}}>rent/bed/wk</span></div>
                  </div>
                  <div className="an-stat an-stat-red">
                    <div className="an-stat-value">$103.40</div>
                    <div className="an-stat-label"><span style={{display:'block'}}>Max CRA</span><span style={{display:'block'}}>Couples p/wk</span></div>
                  </div>
                  <div className="an-stat an-stat-red">
                    <div className="an-stat-value">$73.23</div>
                    <div className="an-stat-label"><span style={{display:'block'}}>Max CRA</span><span style={{display:'block'}}>Singles p/wk</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Carousel (LEFT) + HSS Difference (RIGHT) — SIDE BY SIDE */}
      {propertiesWithPhotos.length >= 1 && (() => {
        const prop = propertiesWithPhotos[carouselIndex];
        if (!prop) return null;
        const r = prop.weekly_rent_per_person;
        const sCRA = Math.min(73.23, Math.max(0, 0.75 * (r - 74.10)));
        const cCRA = Math.min(103.40, Math.max(0, 0.75 * (r - 123.10)));
        const expensiveProps = [...properties]
          .filter(p => p.weekly_rent_per_person > 0)
          .sort((a, b) => {
            const aHasImg = a.images && a.images.length > 0 ? 1 : 0;
            const bHasImg = b.images && b.images.length > 0 ? 1 : 0;
            if (bHasImg !== aHasImg) return bHasImg - aHasImg;
            return b.weekly_rent_per_person - a.weekly_rent_per_person;
          }).slice(0, 5);
        const REA_ADS_COUNT = 5;
        const cheapProps = [...properties].filter(p => p.total_bedrooms >= 4 && p.weekly_rent_per_person > 0 && p.images && p.images.length > 0);
        const dayIndex = Math.floor(Date.now() / 86400000);
        const dailyProps = cheapProps.length >= 5
          ? cheapProps.slice(0, cheapProps.length).sort((a, b) => {
              const h = (s, id) => ((dayIndex * 31 + id.charCodeAt(0) * 17 + id.charCodeAt(2 % id.length) * 7) % 100);
              return h(dayIndex, a.property_id) - h(dayIndex, b.property_id);
            }).slice(0, REA_ADS_COUNT)
          : cheapProps.slice(0, REA_ADS_COUNT);
        const rows = dailyProps.length;
        if (rows < 3) return null;

        return (
          <section className="carousel-and-diff-section" data-testid="carousel-and-diff">
            <div className="container">
              <div className="carousel-diff-layout">

                {/* LEFT: Carousel */}
                <div className="carousel-diff-left" data-testid="property-carousel">
                  <h2 className="section-title">Available Properties</h2>
                  <p className="section-subtitle">Browse shared housing across Australia</p>
                  <div className="carousel-wrapper">
                    <button className="carousel-arrow carousel-prev" onClick={prevSlide} data-testid="carousel-prev" aria-label="Previous property"><ChevronLeft size={22} /></button>
                    <div className="carousel-single-card" onClick={() => navigate(`/properties/${prop.property_id}`)} data-testid={`carousel-card-${prop.property_id}`}>
                      <div className="carousel-card-image">
                        {prop.images && prop.images.length > 0 ? (
                          <img src={cloudinaryOptimize(prop.images[0], 800)} alt={`Property in ${prop.city}`} loading="lazy"
                            onError={(e) => { e.target.style.display='none'; const ph=e.target.parentElement?.querySelector('.carousel-placeholder'); if(ph) ph.style.display='flex'; }} />
                        ) : null}
                        <div className="carousel-placeholder" style={{ display: prop.images && prop.images.length > 0 ? 'none' : 'flex' }}>
                          <Home size={32} color="#9ca3af" /><span>No Image</span>
                        </div>
                      </div>
                      <div className="carousel-card-info">
                        <h3>{prop.city} {prop.state} {prop.postcode || ''}</h3>
                        <div className="carousel-total-rent">
                          {prop.total_bedrooms || '?'} Bedrooms · {prop.total_bathrooms > 0 ? `${prop.total_bathrooms} Bath` : 'Bath TBA'} · Gross Rent ${(Math.round(prop.weekly_rent_per_person) * (prop.total_bedrooms || 1)).toFixed(0)}/wk
                        </div>
                        <div className="carousel-cra-block">
                          <div className="carousel-cra-label">After CRA:</div>
                          <div className="carousel-cra-row"><span className="carousel-cra-type">SINGLES</span><span className="carousel-cra-value">${(r - sCRA).toFixed(0)}/wk</span></div>
                          <div className="carousel-cra-row"><span className="carousel-cra-type">COUPLES</span><span className="carousel-cra-value">${(r - cCRA).toFixed(0)}/wk</span></div>
                        </div>
                        {/* Feature badges */}
                        {(prop.has_pool || prop.has_ac || prop.has_aircon || prop.furnished || prop.pet_friendly || prop.pets_allowed || prop.solar_power || prop.wheelchair_access || prop.close_to_doctor || prop.ceiling_fans || prop.security_systems) && (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', margin: '6px 0' }}>
                            {prop.has_pool && <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', background: '#dbeafe', color: '#2563eb' }}>Pool</span>}
                            {(prop.has_ac || prop.has_aircon) && <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', background: '#dbeafe', color: '#2563eb' }}>A/C</span>}
                            {prop.furnished && <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', background: '#fef3c7', color: '#92400e' }}>Furnished</span>}
                            {(prop.pet_friendly || prop.pets_allowed) && <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', background: '#d1fae5', color: '#059669' }}>Pets OK</span>}
                            {prop.solar_power && <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', background: '#d1fae5', color: '#065f46' }}>Solar</span>}
                            {prop.wheelchair_access && <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', background: '#f3e8ff', color: '#7e22ce' }}>Wheelchair</span>}
                            {prop.close_to_doctor && <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', background: '#fce7f3', color: '#9d174d' }}>Near Doctor</span>}
                            {prop.ceiling_fans && <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', background: '#f0f9ff', color: '#0369a1' }}>Ceiling Fans</span>}
                            {prop.security_systems && <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', background: '#f0fdf4', color: '#166534' }}>Security</span>}
                          </div>
                        )}
                        {/* Agent contact */}
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e5e7eb' }}>
                          {prop.agency_name && (
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a2332', marginBottom: '3px' }}>
                              {prop.agency_logo && <img src={prop.agency_logo} alt={prop.agency_name} style={{ height: '20px', objectFit: 'contain', verticalAlign: 'middle', marginRight: '5px', background: 'white', borderRadius: '3px', padding: '2px' }} onError={e => e.target.style.display='none'} />}
                              {prop.agency_name}
                            </div>
                          )}
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            via <strong style={{ color: '#1a2332' }}>{prop.listing_source_name || (prop.source === 'domain_import' ? 'Domain.com.au' : 'Rent.com.au')}</strong>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {prop.domain_url ? (
                              <a href={prop.domain_url} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: '12px', padding: '4px 10px', background: '#dc2626', color: 'white', borderRadius: '4px', fontWeight: '700', textDecoration: 'none' }}>
                                Contact Agent →
                              </a>
                            ) : (
                              <span style={{ fontSize: '12px', padding: '4px 10px', background: '#e5e7eb', color: '#6b7280', borderRadius: '4px', fontWeight: '700' }}>
                                Listing link unavailable
                              </span>
                            )}
                            <span style={{ fontSize: '11px', color: '#d1d5db' }}>ID: {prop.property_id?.slice(-8)}</span>
                            {prop.listing_date && <span style={{ fontSize: '11px', color: '#9ca3af' }}>{new Date(prop.listing_date).toLocaleDateString('en-AU', { day:'numeric', month:'short' })}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <button className="carousel-arrow carousel-next" onClick={nextSlide} data-testid="carousel-next" aria-label="Next property"><ChevronRight size={22} /></button>
                  </div>
                  <div style={{ textAlign: 'center', marginTop: '8px', color: '#6b7280', fontSize: '13px' }}>Property {carouselIndex + 1} of {propertiesWithPhotos.length}</div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '8px' }}>
                    <button
                      onClick={() => setCarouselPaused(p => !p)}
                      data-testid="carousel-pause"
                      style={{ padding: '5px 14px', borderRadius: '6px', border: '1px solid #d1d5db', background: carouselPaused ? '#1a2332' : 'white', color: carouselPaused ? 'white' : '#374151', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      {carouselPaused ? '▶ Play' : '⏸ Pause'}
                    </button>
                    <button
                      onClick={() => navigate(`/properties/${prop.property_id}`)}
                      data-testid="carousel-goto"
                      style={{ padding: '5px 14px', borderRadius: '6px', border: '1px solid #2563eb', background: '#2563eb', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      Go To Property →
                    </button>
                  </div>
                </div>

                {/* RIGHT: HSS Difference */}
                <HssDifferenceTable dailyProps={dailyProps} />

              </div>
            </div>
          </section>
        );
      })()}

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works">
        <div className="container">
          <h2 className="section-title" data-testid="how-it-works-title">How It Works</h2>
          <p className="section-subtitle">Four simple steps to affordable, companionable living</p>
          <div className="steps-grid">
            <div className="step-card" data-testid="step-1"><div className="step-icon"><ClipboardCheck size={28} /></div><div className="step-number-label">Step 1</div><h3>Apply Online</h3><p>Complete our secure application form about your needs, lifestyle, and preferences. Save and resume anytime.</p></div>
            <div className="step-card" data-testid="step-2"><div className="step-icon"><ShieldCheck size={28} /></div><div className="step-number-label">Step 2</div><h3>Vetting &amp; Review</h3><p>Our team reviews your application, verifies references, and conducts safety checks for everyone&apos;s peace of mind.</p></div>
            <div className="step-card" data-testid="step-3"><div className="step-icon"><UserCheck size={28} /></div><div className="step-number-label">Step 3</div><h3>Get Approved</h3><p>Once approved, create your account and browse properties, groups, and anonymised member profiles.</p></div>
            <div className="step-card" data-testid="step-4"><div className="step-icon"><Search size={28} /></div><div className="step-number-label">Step 4</div><h3>Find Your Match</h3><p>Join a group, chat with potential housemates, and apply together for a property that suits everyone.</p></div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <a href="https://res.cloudinary.com/dcepccggr/raw/upload/v1784153459/hss/docs/HSS-Member-Info-Pack.pdf"
               target="_blank" rel="noopener noreferrer"
               style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 20px', background: '#1a2332', color: 'white', borderRadius: '6px', fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>
              Download HSS Member Information Pack (PDF)
            </a>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="trust-signals" id="hss-features">
        <div className="container">
          <h3 className="trust-title" data-testid="trust-title">Why Seniors Choose Us</h3>
          <div className="trust-badges">
            <div className="trust-badge" data-testid="trust-badge-1"><div className="trust-badge-icon"><DollarSign size={28} /></div><span className="trust-badge-title">Save 40-60% on Rent</span><span className="trust-badge-desc">Plus shared electricity, internet, and household costs</span></div>
            <div className="trust-badge" data-testid="trust-badge-2"><div className="trust-badge-icon"><ShieldCheck size={28} /></div><span className="trust-badge-title">Vetted &amp; Verified</span><span className="trust-badge-desc">Every member is reviewed, reference-checked, and approved</span></div>
            <div className="trust-badge" data-testid="trust-badge-3"><div className="trust-badge-icon"><Users size={28} /></div><span className="trust-badge-title">Compatible Community</span><span className="trust-badge-desc">Matched on lifestyle, interests, and housing preferences</span></div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-section"><h4>About</h4><a href="/about">About Us</a><a href="/contact">Contact</a><a href="/faq">FAQ</a></div>
            <div className="footer-section"><h4>Resources</h4><a href="/support">Help &amp; Support</a><a href="/resources">Downloads</a></div>
            <div className="footer-section"><h4>Legal</h4><a href="/privacy">Privacy Policy</a><a href="/terms">Terms of Service</a></div>
            <div className="footer-section"><h4>Contact Us</h4><p><Phone size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />1300 77 5059</p><p><Mail size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />newmembers@housesharingseniors.com.au</p></div>
          </div>
          <div className="footer-bottom"><p>&copy; 2026 House Sharing Seniors. All rights reserved.</p></div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
